import { bookingPlace } from '@/utils/bookingPlace';
import { localizeServiceName } from '@/utils/serviceNames';
import React, { useMemo } from 'react';
import { Pressable, Text, View } from 'react-native';
import { Image } from 'expo-image';
import { Ionicons } from '@expo/vector-icons';
import { router } from 'expo-router';
import { Appointment } from '@/data/types';
import { fontFamily, radius, shadows, spacing, type, useAppTheme } from '@/theme';
import { StatusBadge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { formatFriendlyDate } from '@/utils/date';
import { formatTaka } from '@/components/ui/PriceTag';
import { formatDuration } from '@/utils/geo';
import { ScaledSheet, useResponsive } from '@/theme/responsive';
import { trText } from '@/i18n/plain';
import { useLanguage } from '@/i18n';

interface Props {
  appointment: Appointment;
  onReschedule?: () => void;
  onCancel?: () => void;
  onDirections?: () => void;
  onBookAgain?: () => void;
  onRate?: () => void;
  onChat?: () => void;
  /** A slim card for long lists (the Bookings tab): about half the height, so more fit on a screen. */
  compact?: boolean;
}

function getCountdownInfo(dateStr: string) {
  if (!dateStr) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr);
  if (!match) return null;
  const targetYear = parseInt(match[1], 10);
  const targetMonth = parseInt(match[2], 10) - 1;
  const targetDay = parseInt(match[3], 10);

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const target = new Date(targetYear, targetMonth, targetDay);

  const diffMs = target.getTime() - today.getTime();
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return { label: trText('TODAY', 'আজ'), isToday: true, isTomorrow: false };
  if (diffDays === 1) return { label: trText('TOMORROW', 'আগামীকাল'), isToday: false, isTomorrow: true };
  if (diffDays > 1 && diffDays <= 7) return { label: trText(`IN ${diffDays} DAYS`, `${diffDays} দিন পরে`), isToday: false, isTomorrow: false };
  return null;
}

export function AppointmentCard({
  appointment: apt,
  onReschedule,
  onCancel,
  onDirections,
  onBookAgain,
  onRate,
  onChat,
  compact,
}: Props) {
  useLanguage(); // redraw when the language changes (trText is read at call time)
  const { colors, isDark } = useAppTheme();
  // A solo stylist has no shop: the adapter's fallback name is not worth showing.
  const place = bookingPlace(apt);
  const shopTitle = place.title;
  const goToDetails = () => router.push({ pathname: '/appointment/[id]', params: { id: apt.id } });

  const countdown = useMemo(() => {
    if (apt.status === 'PENDING' || apt.status === 'CONFIRMED' || apt.status === 'IN_PROGRESS') {
      return getCountdownInfo(apt.date);
    }
    return null;
  }, [apt.date, apt.status]);

  const serviceNames = apt.services.map((s) => localizeServiceName(s.name)).join(', ');
  const totalDuration = apt.services.reduce((acc, s) => acc + (s.durationMin || apt.durationMin || 30), 0);

  if (compact) {
    return (
      <CompactCard
        apt={apt}
        countdown={countdown}
        serviceNames={serviceNames}
        totalDuration={totalDuration}
        goToDetails={goToDetails}
        onReschedule={onReschedule}
        onCancel={onCancel}
        onDirections={onDirections}
        onBookAgain={onBookAgain}
        onRate={onRate}
      />
    );
  }

  return (
    <Pressable
      accessibilityRole="button"
      onPress={goToDetails}
      style={({ pressed }) => [
        styles.card,
        {
          backgroundColor: colors.surface,
          borderColor: isDark ? colors.border : '#E2E8F0',
        },
        pressed && { opacity: 0.96, transform: [{ scale: 0.995 }] },
      ]}>
      {/* TOP STRIP: Booking Code + Live Urgency Tag + Status */}
      <View style={styles.topStrip}>
        <View style={styles.topStripLeft}>
          <View style={[styles.codeBadge, { backgroundColor: colors.surfaceMuted, borderColor: colors.border }]}>
            <Ionicons name="ticket-outline" size={12} color={colors.accent} />
            <Text style={[styles.codeText, { color: colors.textSecondary }]}>#{apt.bookingCode || apt.id.slice(0, 6).toUpperCase()}</Text>
          </View>

          {countdown && (
            <View
              style={[
                styles.countdownPill,
                countdown.isToday
                  ? { backgroundColor: colors.successSoft, borderColor: colors.success }
                  : countdown.isTomorrow
                  ? { backgroundColor: colors.warningSoft, borderColor: colors.warning }
                  : { backgroundColor: colors.accentSoft, borderColor: colors.accent },
              ]}>
              {countdown.isToday && <View style={[styles.pulseDot, { backgroundColor: colors.success }]} />}
              <Text
                style={[
                  styles.countdownText,
                  {
                    color: countdown.isToday
                      ? colors.success
                      : countdown.isTomorrow
                      ? colors.warning
                      : colors.accent,
                  },
                ]}>
                {countdown.label}
              </Text>
            </View>
          )}
        </View>

        <StatusBadge status={apt.status} />
      </View>

      {/* SHOP & STYLIST PROFILE ROW */}
      <View style={styles.header}>
        {apt.shopImage ? (
          <Image
            source={{ uri: apt.shopImage }}
            style={[styles.shopImage, { backgroundColor: colors.surfaceMuted }]}
            contentFit="cover"
            transition={150}
          />
        ) : (
          <View style={[styles.shopPlaceholder, { backgroundColor: colors.accentSoft }]}>
            <Ionicons name="storefront" size={24} color={colors.accent} />
          </View>
        )}

        <View style={styles.headerInfo}>
          <View style={styles.shopNameRow}>
            <Text style={[type.titleLg, styles.shopName, { color: colors.textPrimary }]} numberOfLines={1}>
              {shopTitle}
            </Text>
            <Ionicons name="checkmark-circle" size={15} color={colors.accent} style={{ marginLeft: 4 }} />
          </View>

          <View style={[styles.stylistPill, { backgroundColor: colors.surfaceMuted }]}>
            <Ionicons name="cut-outline" size={12} color={colors.accent} />
            <Text style={[styles.stylistText, { color: colors.textSecondary }]} numberOfLines={1}>
              {trText('Stylist:', 'স্টাইলিস্ট:')} <Text style={{ color: colors.textPrimary, fontFamily: fontFamily.bold }}>{apt.stylistName}</Text>
            </Text>
          </View>

          <Text style={[type.bodySm, styles.servicesText, { color: colors.textSecondary }]} numberOfLines={1}>
            {serviceNames}
          </Text>
        </View>
      </View>

      {/* DATE, TIME & PRICE RIBBON */}
      <View style={[styles.ribbon, { backgroundColor: colors.surfaceMuted, borderColor: colors.border }]}>
        <View style={styles.ribbonLeft}>
          <View style={styles.ribbonItem}>
            <Ionicons name="calendar" size={14} color={colors.accent} />
            <Text style={[styles.ribbonText, { color: colors.textPrimary }]}>{formatFriendlyDate(apt.date)}</Text>
          </View>

          <View style={[styles.ribbonDivider, { backgroundColor: colors.border }]} />

          <View style={styles.ribbonItem}>
            <Ionicons name="time" size={14} color={colors.accent} />
            <Text style={[styles.ribbonText, { color: colors.textPrimary }]}>{apt.time}</Text>
          </View>

          {totalDuration > 0 && (
            <>
              <View style={[styles.ribbonDivider, { backgroundColor: colors.border }]} />
              <Text style={[styles.durationText, { color: colors.textTertiary }]}>{formatDuration(totalDuration)}</Text>
            </>
          )}
        </View>

        <View style={styles.ribbonRight}>
          <Text style={[styles.priceLabel, { color: colors.textTertiary }]}>{trText('Total', 'টোটাল')}</Text>
          <Text style={[styles.priceValue, { color: colors.accent }]}>{formatTaka(apt.total)}</Text>
        </View>
      </View>

      {/* CANCELLATION & REVIEW DETAILS */}
      {apt.status === 'CANCELLED' && apt.cancellationReason && (
        <View style={[styles.noteBox, { backgroundColor: isDark ? 'rgba(239, 68, 68, 0.12)' : '#FEF2F2', borderColor: isDark ? 'rgba(239, 68, 68, 0.3)' : '#FEE2E2' }]}>
          <Ionicons name="alert-circle-outline" size={14} color={colors.error} />
          <Text style={[type.caption, { color: colors.error, flex: 1 }]}>Reason: {apt.cancellationReason}</Text>
        </View>
      )}

      {apt.status === 'COMPLETED' && (
        <View style={styles.reviewStatusRow}>
          <Ionicons
            name={apt.reviewed ? 'star' : 'star-outline'}
            size={14}
            color={apt.reviewed ? colors.star : colors.textTertiary}
          />
          <Text style={[type.caption, { color: apt.reviewed ? colors.success : colors.textTertiary, flexShrink: 1 }]}>
            {apt.reviewed ? 'You rated & reviewed this visit' : 'You haven’t reviewed this visit yet'}
          </Text>
        </View>
      )}

      {/* ACTION BUTTONS */}
      {(apt.status === 'PENDING' || apt.status === 'CONFIRMED' || apt.status === 'IN_PROGRESS') && (
        <View style={styles.actionsRow}>
          {onDirections && (
            <View style={styles.actionBtnWrap}>
              <Button
                label={trText('Directions', 'দিকনির্দেশনা')}
                variant="outlined"
                size="sm"
                icon="navigate-outline"
                onPress={onDirections}
              />
            </View>
          )}

          {onReschedule && (
            <View style={styles.actionBtnWrap}>
              <Button
                label={trText('Reschedule', 'রিশিডিউল')}
                variant="outlined"
                size="sm"
                icon="calendar-outline"
                onPress={onReschedule}
              />
            </View>
          )}

          {onCancel && (
            <View style={styles.actionBtnWrap}>
              <Button
                label={trText('Cancel', 'ক্যান্সেল')}
                variant="danger"
                size="sm"
                icon="close-circle-outline"
                onPress={onCancel}
              />
            </View>
          )}
        </View>
      )}

      {apt.status === 'COMPLETED' && (
        <View style={styles.actionsRow}>
          <View style={styles.actionBtnWrap}>
            <Button
              label={apt.reviewed ? 'View Review' : 'Rate Visit'}
              variant="outlined"
              size="sm"
              icon={apt.reviewed ? 'star' : 'star-outline'}
              onPress={onRate}
            />
          </View>
          {onBookAgain && (
            <View style={styles.actionBtnWrap}>
              <Button
                label={trText('Book Again', 'আবার বুক করুন')}
                variant="primary"
                size="sm"
                icon="repeat"
                onPress={onBookAgain}
              />
            </View>
          )}
        </View>
      )}

      {(apt.status === 'CANCELLED' || apt.status === 'NO_SHOW') && onBookAgain && (
        <View style={styles.actionsRow}>
          <View style={styles.actionBtnWrap}>
            <Button
              label={trText('Rebook Appointment', 'আবার অ্যাপয়েন্টমেন্ট বুক করুন')}
              variant="primary"
              size="sm"
              icon="calendar"
              onPress={onBookAgain}
            />
          </View>
        </View>
      )}
    </Pressable>
  );
}

type MiniTone = 'outlined' | 'danger' | 'primary';

/** One small action pill; a row of three fits across a phone without wrapping. */
function MiniAction({ label, icon, tone, onPress, showIcon, iconOnly }: { label: string; icon: keyof typeof Ionicons.glyphMap; tone: MiniTone; onPress?: () => void; showIcon: boolean; iconOnly?: boolean }) {
  const { colors } = useAppTheme();
  const palette =
    tone === 'primary'
      ? { bg: colors.accent, fg: colors.textOnAccent, border: 'transparent' }
      : tone === 'danger'
      ? { bg: colors.errorSoft, fg: colors.error, border: 'transparent' }
      : { bg: 'transparent', fg: colors.textPrimary, border: colors.borderStrong };
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      onPress={onPress}
      style={({ pressed }) => [styles.miniAction, iconOnly && styles.miniActionIconOnly, { backgroundColor: palette.bg, borderColor: palette.border }, pressed && { opacity: 0.85 }]}>
      {showIcon || iconOnly ? <Ionicons name={icon} size={iconOnly ? 18 : 14} color={palette.fg} /> : null}
      {iconOnly ? null : (
        <Text style={[styles.miniActionText, { color: palette.fg }]} numberOfLines={1}>
          {label}
        </Text>
      )}
    </Pressable>
  );
}

interface CompactProps {
  apt: Appointment;
  countdown: { label: string; isToday: boolean; isTomorrow: boolean } | null;
  serviceNames: string;
  totalDuration: number;
  goToDetails: () => void;
  onReschedule?: () => void;
  onCancel?: () => void;
  onDirections?: () => void;
  onBookAgain?: () => void;
  onRate?: () => void;
}

/** The slim card: shop and status on top, stylist and services under it, one line of date, time and
 *  total, and a single row of actions. */
function CompactCard({ apt, countdown, serviceNames, totalDuration, goToDetails, onReschedule, onCancel, onDirections, onBookAgain, onRate }: CompactProps) {
  const { colors, isDark } = useAppTheme();
  const { contentWidth } = useResponsive();
  const open = apt.status === 'PENDING' || apt.status === 'CONFIRMED' || apt.status === 'IN_PROGRESS';
  const showIcons = contentWidth >= 360;
  // Three buttons in a row: on a small screen Directions and Cancel shrink to their icons so
  // Reschedule keeps its full word; once there is room (about 440 and up) all three show icon and word.
  const canReschedule = !!onReschedule && !apt.rescheduleRequest;
  const iconOnly = !!(open && onDirections && canReschedule && onCancel) && contentWidth < 440;
  const place = bookingPlace(apt);
  const detail = [place.byline, serviceNames].filter(Boolean).join(' · ');
  const shopTitle = place.title;

  return (
    <Pressable
      accessibilityRole="button"
      onPress={goToDetails}
      style={({ pressed }) => [
        styles.slimCard,
        { backgroundColor: colors.surface, borderColor: isDark ? colors.border : '#E2E8F0' },
        pressed && { opacity: 0.96 },
      ]}>
      <View style={styles.slimTop}>
        {apt.shopImage ? (
          <Image source={{ uri: apt.shopImage }} style={[styles.slimImage, { backgroundColor: colors.surfaceMuted }]} contentFit="cover" transition={150} />
        ) : (
          <View style={[styles.slimImage, styles.slimImagePlaceholder, { backgroundColor: colors.accentSoft }]}>
            <Ionicons name="storefront" size={20} color={colors.accent} />
          </View>
        )}
        <View style={styles.slimInfo}>
          <Text style={[type.titleLg, styles.slimName, { color: colors.textPrimary }]} numberOfLines={1}>
            {shopTitle}
          </Text>
          <Text style={[styles.slimDetail, { color: colors.textSecondary }]} numberOfLines={1}>
            {detail}
          </Text>
        </View>
        <View style={styles.slimBadges}>
          <StatusBadge status={apt.status} />
          {countdown ? (
            <Text style={[styles.slimCountdown, { color: countdown.isToday ? colors.success : countdown.isTomorrow ? colors.warning : colors.accent }]}>{countdown.label}</Text>
          ) : null}
        </View>
      </View>

      <View style={[styles.slimRow, { borderTopColor: colors.border }]}>
        <Ionicons name="calendar" size={13} color={colors.accent} />
        <Text style={[styles.slimRowText, { color: colors.textPrimary }]} numberOfLines={1}>
          {formatFriendlyDate(apt.date)} · {apt.time}
          {totalDuration > 0 ? ` · ${formatDuration(totalDuration)}` : ''}
        </Text>
        <Text style={[styles.slimPrice, { color: colors.accent }]}>{formatTaka(apt.total)}</Text>
      </View>

      {open && apt.rescheduleRequest ? (
        <View style={styles.slimPending}>
          <Ionicons name="hourglass-outline" size={12} color={colors.warning} />
          <Text style={[type.caption, { color: colors.warning, flexShrink: 1 }]} numberOfLines={1}>
            {trText('Waiting for the stylist:', 'স্টাইলিস্টের উত্তরের অপেক্ষায়:')} {formatFriendlyDate(apt.rescheduleRequest.date)} · {apt.rescheduleRequest.time}
          </Text>
        </View>
      ) : null}

      {apt.status === 'CANCELLED' && apt.cancellationReason ? (
        <Text style={[type.caption, { color: colors.error }]} numberOfLines={1}>
          {trText('Reason:', 'কারণ:')} {apt.cancellationReason}
        </Text>
      ) : null}

      {open && (onDirections || canReschedule || onCancel) ? (
        <View style={styles.slimActions}>
          {onDirections ? <MiniAction label={trText('Directions', 'দিকনির্দেশনা')} icon="navigate-outline" tone="outlined" onPress={onDirections} showIcon={showIcons} iconOnly={iconOnly} /> : null}
          {canReschedule ? <MiniAction label={trText('Reschedule', 'রিশিডিউল')} icon="calendar-outline" tone="outlined" onPress={onReschedule} showIcon={showIcons} /> : null}
          {onCancel ? <MiniAction label={trText('Cancel', 'ক্যান্সেল')} icon="close-circle-outline" tone="danger" onPress={onCancel} showIcon={showIcons} iconOnly={iconOnly} /> : null}
        </View>
      ) : null}

      {apt.status === 'COMPLETED' ? (
        <View style={styles.slimActions}>
          <MiniAction
            label={apt.reviewed ? trText('View Review', 'রিভিউ দেখুন') : trText('Rate Visit', 'রেটিং দিন')}
            icon={apt.reviewed ? 'star' : 'star-outline'}
            tone="outlined"
            onPress={onRate}
            showIcon={showIcons}
          />
          {onBookAgain ? <MiniAction label={trText('Book Again', 'আবার বুক করুন')} icon="repeat" tone="primary" onPress={onBookAgain} showIcon={showIcons} /> : null}
        </View>
      ) : null}

      {(apt.status === 'CANCELLED' || apt.status === 'NO_SHOW') && onBookAgain ? (
        <View style={styles.slimActions}>
          <MiniAction label={trText('Rebook', 'আবার বুক করুন')} icon="calendar" tone="primary" onPress={onBookAgain} showIcon={showIcons} />
        </View>
      ) : null}
    </Pressable>
  );
}

const styles = ScaledSheet.create({
  slimCard: {
    borderRadius: radius.lg,
    borderWidth: 1,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm + 2,
    gap: spacing.sm,
    overflow: 'hidden',
    ...shadows.card,
  },
  slimTop: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  slimImage: { width: 44, height: 44, borderRadius: radius.md },
  slimImagePlaceholder: { alignItems: 'center', justifyContent: 'center' },
  slimInfo: { flex: 1, gap: 1 },
  slimName: { fontSize: 15, letterSpacing: -0.2 },
  slimDetail: { fontSize: 12 },
  slimBadges: { alignItems: 'flex-end', gap: 2 },
  slimCountdown: { fontSize: 10, fontFamily: fontFamily.extraBold, letterSpacing: 0.3 },
  slimRow: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingTop: spacing.sm, borderTopWidth: 1 },
  slimRowText: { flex: 1, fontSize: 12.5, fontFamily: fontFamily.semiBold },
  slimPrice: { fontFamily: fontFamily.extraBold, fontSize: 14 },
  slimPending: { flexDirection: 'row', alignItems: 'center', gap: 5 },
  slimActions: { flexDirection: 'row', gap: spacing.sm },
  miniAction: { flex: 1, minWidth: 0, height: 34, borderRadius: radius.md, borderWidth: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 5, paddingHorizontal: 6 },
  miniActionIconOnly: { flexGrow: 0, flexShrink: 0, flexBasis: 46, width: 46, paddingHorizontal: 0 },
  miniActionText: { fontSize: 12.5, fontFamily: fontFamily.semiBold, flexShrink: 1 },
  card: {
    borderRadius: radius.xl,
    borderWidth: 1,
    padding: spacing.lg,
    gap: spacing.md,
    overflow: 'hidden',
    ...shadows.card,
  },
  topStrip: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  topStripLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexShrink: 1,
  },
  codeBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: radius.pill,
    borderWidth: 1,
    flexShrink: 1,
  },
  codeText: {
    fontSize: 11,
    fontFamily: fontFamily.semiBold,
    letterSpacing: 0.5,
    flexShrink: 1,
  },
  countdownPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: radius.pill,
    borderWidth: 1,
    flexShrink: 1,
  },
  pulseDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  countdownText: {
    fontSize: 10.5,
    fontFamily: fontFamily.extraBold,
    letterSpacing: 0.3,
    flexShrink: 1,
  },
  header: {
    flexDirection: 'row',
    gap: spacing.md,
    alignItems: 'flex-start',
  },
  shopImage: {
    width: 58,
    height: 58,
    borderRadius: radius.md,
  },
  shopPlaceholder: {
    width: 58,
    height: 58,
    borderRadius: radius.md,
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerInfo: {
    flex: 1,
    gap: 3,
  },
  shopNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  shopName: {
    fontSize: 16,
    letterSpacing: -0.2,
    flexShrink: 1,
  },
  stylistPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    alignSelf: 'flex-start',
    paddingHorizontal: 7,
    paddingVertical: 2,
    borderRadius: radius.xs,
    marginTop: 2,
    maxWidth: '100%',
  },
  stylistText: {
    fontSize: 12,
    flexShrink: 1,
  },
  servicesText: {
    fontSize: 13,
    marginTop: 2,
  },
  ribbon: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.md,
    paddingVertical: 9,
    borderRadius: radius.md,
    borderWidth: 1,
  },
  // Date, time and duration wrap onto a second line on a narrow phone instead of pushing the total off.
  ribbonLeft: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 8,
    flexShrink: 1,
  },
  ribbonItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    flexShrink: 1,
  },
  ribbonDivider: {
    width: 1,
    height: 12,
  },
  ribbonText: {
    fontFamily: fontFamily.bold,
    fontSize: 13,
    flexShrink: 1,
  },
  durationText: {
    fontFamily: fontFamily.medium,
    fontSize: 12,
    flexShrink: 1,
  },
  ribbonRight: {
    alignItems: 'flex-end',
  },
  priceLabel: {
    fontSize: 9.5,
    fontFamily: fontFamily.medium,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  priceValue: {
    fontFamily: fontFamily.extraBold,
    fontSize: 14.5,
  },
  noteBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    padding: spacing.sm,
    borderRadius: radius.sm,
    borderWidth: 1,
  },
  reviewStatusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 2,
  },
  // Three buttons need ~125pt each: side by side where they fit, otherwise two and one.
  actionsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
    marginTop: 2,
  },
  actionBtnWrap: {
    flex: 1,
    minWidth: 120,
  },
});
